import { useState, useRef } from "react";
import { S, NAVY, GOLD, BORDER, WHITE, RED, SLATE, BEIGE, BEIGE_MID, fmt, fmtVal, initials, uid, sameId } from "../constants";
import { Pagination, MultiSelect, ActivityFeed, CustomFieldInput, AttachmentsPanel } from "./shared";
import { EmailComposeModal } from "./modals";

export function LeadsList({
  savedViews, activeViewId, newViewName, showSaveView, search, filterStage, filterUser, filterSource, filterService,
  stages, users, leadSources, services, checkedLeads, paginatedLeads, filteredLeads, leadPage, totalLeadPages,
  leadSortColumn, leadSortDir,
  setActiveViewId, setFilterStage, setFilterUser, setFilterSource, setFilterService, setLeadPage, setNewViewName,
  setShowSaveView, setSearch, setCheckedLeads, setLeadSortColumn, setLeadSortDir, setBulkStep, setShowBulkUpload,
  setShowAddLead, saveSavedViews, showToast,
  updateLeadStage, setSelectedLead, setEditingLead, setPage, deleteLead,
  leads, saveLeads,
}) {
  const applyView = (view) => {
    setActiveViewId(view.id);
    setFilterStage(Array.isArray(view.filters.stage) ? view.filters.stage : []);
    setFilterUser(Array.isArray(view.filters.user) ? view.filters.user : []);
    setFilterSource(Array.isArray(view.filters.source) ? view.filters.source : []);
    setFilterService(Array.isArray(view.filters.service) ? view.filters.service : []);
    setLeadPage(1);
  };
  const saveCurrentView = () => {
    if (!newViewName.trim()) return;
    const v = { id: uid(), name: newViewName.trim(), filters: { stage: filterStage, user: filterUser, source: filterSource, service: filterService }, isDefault: false };
    const updated = [...savedViews, v];
    saveSavedViews(updated);
    setActiveViewId(v.id);
    setNewViewName("");
    setShowSaveView(false);
    showToast("View saved — " + v.name);
  };
  const deleteView = (id) => {
    const updated = savedViews.filter(v => v.id !== id);
    saveSavedViews(updated);
    if (activeViewId === id) applyView(updated[0]);
  };
  const activeFilters = [
    ...filterStage,
    ...filterUser.map(id => users.find(u => u.id === id)?.name).filter(Boolean),
    ...filterSource,
    ...filterService,
  ];
  const [editingFollowUpId, setEditingFollowUpId] = useState(null);
  const searchInputRef = useRef(null);
  const leadColumns = [
    { key: "contactName", label: "Contact Name" },
    { key: "clubName", label: "Club Name" },
    { key: "stage", label: "Stage" },
    { key: "source", label: "Source" },
    { key: "followUp", label: "Follow-up Date" },
    { key: "createdAt", label: "Created Date" },
    { key: "value", label: "Value" },
  ];
  const handleSortClick = (column) => {
    if (leadSortColumn !== column) {
      setLeadSortColumn(column);
      setLeadSortDir("asc");
    } else if (leadSortDir === "asc") {
      setLeadSortDir("desc");
    } else {
      setLeadSortColumn(null);
      setLeadSortDir("asc");
    }
    setLeadPage(1);
  };

  const handleFollowUpChange = (leadId, newDate) => {
    if (!leads || !saveLeads) return;
    const updated = leads.map(l => sameId(l.id, leadId) ? { ...l, followUp: newDate || "" } : l);
    saveLeads(updated);
    setEditingFollowUpId(null);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: SLATE, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 4 }}>Views:</span>
        {savedViews.map(v => (
          <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 0, borderRadius: 8, border: "1px solid " + (activeViewId === v.id ? GOLD : BORDER), overflow: "hidden" }}>
            <button onClick={() => applyView(v)} style={{ padding: "5px 12px", background: activeViewId === v.id ? "#FFFCF5" : WHITE, color: activeViewId === v.id ? GOLD : SLATE, border: "none", fontSize: 12, fontWeight: activeViewId === v.id ? 700 : 400, cursor: "pointer", fontFamily: "Open Sans,sans-serif" }}>{v.name}</button>
            {!v.isDefault && <button onClick={() => deleteView(v.id)} style={{ padding: "5px 8px", background: activeViewId === v.id ? "#FFFCF5" : WHITE, color: SLATE, border: "none", borderLeft: "1px solid " + BORDER, fontSize: 11, cursor: "pointer", lineHeight: 1 }}>×</button>}
          </div>
        ))}
        {showSaveView ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input value={newViewName} onChange={e => setNewViewName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveCurrentView()} placeholder="View name..." style={{ ...S.input, width: 140, padding: "5px 10px", fontSize: 12 }} autoFocus />
            <button onClick={saveCurrentView} style={{ ...S.btnPrimary, padding: "5px 12px", fontSize: 12 }}>Save</button>
            <button onClick={() => setShowSaveView(false)} style={{ ...S.btnGhost, padding: "5px 10px", fontSize: 12 }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowSaveView(true)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px dashed " + BORDER, background: "transparent", color: SLATE, fontSize: 12, cursor: "pointer", fontFamily: "Open Sans,sans-serif" }}>+ Save View</button>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: SLATE, fontSize: 13, pointerEvents: "none" }}>🔍</span>
          <input
            ref={searchInputRef}
            value={search}
            onChange={e => { setSearch(e.target.value); setLeadPage(1); }}
            placeholder="Search leads..."
            style={{ ...S.input, width: 220, paddingLeft: 32, paddingRight: 28 }}
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => { setSearch(""); setLeadPage(1); requestAnimationFrame(() => searchInputRef.current?.focus()); }}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#5B6770",
                fontSize: "14px",
              }}
            >
              ×
            </button>
          )}
        </div>
        <MultiSelect label="All Stages" options={stages.map(s => ({ value: s, label: s }))} value={filterStage} onChange={(v) => { setFilterStage(v); setLeadPage(1); }} />
        <MultiSelect label="All Reps" options={users.map(u => ({ value: u.id, label: u.name }))} value={filterUser} onChange={(v) => { setFilterUser(v); setLeadPage(1); }} />
        <MultiSelect label="All Sources" options={leadSources.map(s => ({ value: s, label: s }))} value={filterSource} onChange={(v) => { setFilterSource(v); setLeadPage(1); }} />
        <MultiSelect label="All Services" options={services.map(s => ({ value: s, label: s }))} value={filterService} onChange={(v) => { setFilterService(v); setLeadPage(1); }} />
        {activeFilters.length > 0 && (
          <button onClick={() => { setFilterStage([]); setFilterUser([]); setFilterSource([]); setFilterService([]); setLeadPage(1); }} style={{ fontSize: 12, color: RED, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "Open Sans,sans-serif" }}>
            Clear ({activeFilters.length})
          </button>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {checkedLeads.length > 0 && <button style={S.btnGold}>✉ Bulk Email ({checkedLeads.length})</button>}
          <button onClick={() => { setBulkStep("upload"); setShowBulkUpload(true); }} style={S.btnGold}>⬆ Import CSV</button>
          <button onClick={() => setShowAddLead(true)} style={S.btnPrimary}>+ Add Lead</button>
        </div>
      </div>
      {activeFilters.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {activeFilters.map((f, i) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, background: NAVY, color: WHITE, fontWeight: 600 }}>{f}</span>)}
        </div>
      )}
      <div style={{ background: BEIGE, padding: "0 0 16px 0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "44px 1.4fr 1.6fr 1fr 1fr 1.1fr 1.1fr 0.9fr", padding: "0 20px", height: 38, alignItems: "center", background: NAVY, borderRadius: 8, marginBottom: 10, gap: 4 }}>
          <input type="checkbox" checked={paginatedLeads.length > 0 && paginatedLeads.every(l => checkedLeads.some(cid => sameId(cid, l.id)))} onChange={() => setCheckedLeads(paginatedLeads.length > 0 && paginatedLeads.every(l => checkedLeads.some(cid => sameId(cid, l.id))) ? [] : paginatedLeads.map(l => l.id))} style={{ width: 15, height: 15, borderRadius: 4, accentColor: GOLD, cursor: "pointer" }} />
          {leadColumns.map((col) => {
            const isActive = leadSortColumn === col.key;
            const arrow = isActive ? (leadSortDir === "asc" ? "↑" : "↓") : "";
            return (
              <div
                key={col.key}
                onClick={() => handleSortClick(col.key)}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(197,162,93,0.12)"; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: isActive ? GOLD : "rgba(197,162,93,0.65)",
                  fontFamily: "'Open Sans', sans-serif",
                  cursor: "pointer",
                  userSelect: "none",
                  borderRadius: 4,
                  padding: "4px 6px",
                  background: isActive ? "rgba(197,162,93,0.2)" : "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {col.label}
                {arrow && <span style={{ fontSize: 11, lineHeight: 1 }}>{arrow}</span>}
              </div>
            );
          })}
        </div>
        {paginatedLeads.map(lead => {
          const leadInitials = initials(lead.contactName);
          const isOverdue = lead.followUp && new Date(lead.followUp) < new Date();
          const stageKey = lead.stage || "New Lead";
          const stageBadge = { "Demo Scheduled": { background: "#e8f5ea", color: "#1a7a30" }, "New Lead": { background: "#e8eef8", color: "#1a4a8a" }, "Proposal Sent": { background: "#fdf4e0", color: "#9a6010" }, "Contacted": { background: "#f0edf8", color: "#5a3a8a" }, "Closed Won": { background: "#e8f5ea", color: "#1a7a30" }, "Closed Lost": { background: "#ffebee", color: "#c62828" } }[stageKey] || { background: BEIGE_MID, color: SLATE };
          return (
            <div key={lead.id} onClick={() => { setSelectedLead(lead); setEditingLead(false); setPage("lead-detail"); }} onMouseEnter={e => { e.currentTarget.style.background = "#f8f8f8"; }} onMouseLeave={e => { e.currentTarget.style.background = "#FFFFFF"; }} style={{ display: "grid", gridTemplateColumns: "44px 1.4fr 1.6fr 1fr 1fr 1.1fr 1.1fr 0.9fr", alignItems: "center", gap: 4, background: "#FFFFFF", borderRadius: 10, margin: "8px 0", padding: "16px 20px", border: "1px solid #E8E3D8", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", cursor: "pointer" }}>
              <input type="checkbox" checked={checkedLeads.some(cid => sameId(cid, lead.id))} onChange={() => setCheckedLeads(prev => prev.some(x => sameId(x, lead.id)) ? prev.filter(x => !sameId(x, lead.id)) : [...prev, lead.id])} onClick={e => e.stopPropagation()} style={{ width: 15, height: 15, borderRadius: 4, accentColor: NAVY, cursor: "pointer" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: "#e8eef8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: NAVY, fontFamily: "'Merriweather', serif" }}>{leadInitials}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, fontFamily: "'Open Sans', sans-serif" }}>{lead.contactName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#a0aab4", marginTop: 1 }}>{lead.title || "Contact"}</div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{lead.clubName || "—"}</div>
              <div>
                <select value={stages.includes(lead.stage) ? lead.stage : (stages[0] || "")} onClick={e => e.stopPropagation()} onChange={e => updateLeadStage(lead.id, e.target.value)} style={{ background: stageBadge.background, color: stageBadge.color, border: "none", borderRadius: 4, padding: "3px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Open Sans', sans-serif" }}>
                  {stages.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 11.5, color: "#8090a4" }}>{lead.source || "—"}</div>
              <div onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: isOverdue ? "#c0392b" : SLATE, minWidth: 90 }}>
                {sameId(editingFollowUpId, lead.id) ? (
                  <input
                    type="date"
                    autoFocus
                    value={lead.followUp || ""}
                    onChange={e => handleFollowUpChange(lead.id, e.target.value)}
                    onBlur={() => setEditingFollowUpId(null)}
                    style={{ ...S.input, padding: "4px 6px", fontSize: 11, width: "100%", boxSizing: "border-box" }}
                  />
                ) : (
                  <div onClick={() => setEditingFollowUpId(lead.id)} style={{ cursor: "pointer", padding: "4px 0", borderRadius: 4 }} title="Click to edit">
                    {lead.followUp ? `📅 ${new Date(lead.followUp + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : <span style={{ color: "#c8d4dd" }}>—</span>}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: "#b0bcc8", whiteSpace: "nowrap" }}>{lead.createdAt ? new Date(lead.createdAt + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</div>
              <div style={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>{lead.value ? fmtVal(lead.value) : "—"}</div>
            </div>
          );
        })}
        {filteredLeads.length === 0 && <div style={{ padding: 48, textAlign: "center", color: SLATE, fontSize: 13 }}>No leads found.</div>}
        <Pagination cur={leadPage} total={totalLeadPages} onChange={setLeadPage} />
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
export function LeadDetail({
  selectedLead, editingLead, editLeadForm, leads, stages, leadSources, services, users, customLeadFields,
  setPage, setEditingLead, setEditLeadForm, setSelectedLead, setActivityTarget, setShowAddActivity,
  setConvertData, setShowConvert, setProposalRecord, setShowProposalModal,
  updateLeadStage, updateLeadCustomField, deleteLead, saveLeads,
  showEmailModal, setShowEmailModal, showToast, currentUser, saveClients, clients, emailTemplates, emailSignature,
}) {
  if (!selectedLead) return null;
  const l = selectedLead;
  const activityItems = Array.isArray(l.activity) ? l.activity : [];
  const stageSelectValue = Array.isArray(stages) && stages.includes(l.stage) ? l.stage : (stages?.[0] || "");
  const dealNum = Number(l.value);
  const hasDealValue = l.value != null && l.value !== "" && !Number.isNaN(dealNum);
  const rep = users.find(u => sameId(u.id, l.assignedTo));
  const isEditing = editingLead;
  const startEdit = () => { setEditLeadForm({ ...l, customFields: l.customFields || {}, attachments: l.attachments || [] }); setEditingLead(true); };
  const cancelEdit = () => setEditingLead(false);
  const saveEdit = () => {
    const updated = leads.map(x => sameId(x.id, l.id) ? { ...x, ...editLeadForm } : x);
    saveLeads(updated);
    setSelectedLead({ ...l, ...editLeadForm });
    setEditingLead(false);
    showToast("Lead updated");
  };
  const F = editLeadForm;
  const set = (k, v) => setEditLeadForm(f => ({ ...f, [k]: v }));
  const handleAddAttachment = (attachment) => {
    const updated = leads.map(lead => sameId(lead.id, l.id) ? { ...lead, attachments: [...(lead.attachments || []), attachment] } : lead);
    saveLeads(updated);
    const next = updated.find(lead => sameId(lead.id, l.id));
    if (next) setSelectedLead(next);
  };
  const handleDeleteAttachment = (attachmentId) => {
    const updated = leads.map(lead => sameId(lead.id, l.id) ? { ...lead, attachments: (lead.attachments || []).filter(a => a.id !== attachmentId) } : lead);
    saveLeads(updated);
    const next = updated.find(lead => sameId(lead.id, l.id));
    if (next) setSelectedLead(next);
  };
  return (
    <div>
      <button onClick={() => { setPage("leads"); setEditingLead(false); }} style={S.backBtn}>{"< Back to Leads"}</button>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {!isEditing ? (<>
          <button onClick={() => { setConvertData({ startDate: new Date().toISOString().split("T")[0], renewalDate: "", status: "Active" }); setShowConvert(true); }} style={S.btnGreen}>Convert to Client</button>
          <button onClick={() => { setProposalRecord(l); setShowProposalModal(true); }} style={S.btnGold}>📄 Generate Proposal</button>
          <button onClick={startEdit} style={S.btnGold}>✏ Edit Profile</button>
          <select value={stageSelectValue} onChange={e => updateLeadStage(l.id, e.target.value)} style={{ ...S.stageSelect(stageSelectValue || l.stage), padding: "8px 14px", fontSize: 13, minWidth: 160 }}>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
          <button onClick={() => setShowEmailModal(true)} style={S.btnGold}>✉ Email</button>
          <button onClick={() => deleteLead(l.id)} style={{ ...S.btnGhost, color: RED, borderColor: "#FCA5A5" }}>🗑</button>
        </>) : (<>
          <button onClick={cancelEdit} style={S.btnGhost}>Cancel</button>
          <button onClick={saveEdit} style={S.btnPrimary}>Save Changes</button>
        </>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>
        <div style={{ ...S.card, padding: "32px 36px" }}>
          {!isEditing ? (<>
            <div style={{ display: "flex", gap: 20, marginBottom: 28 }}>
              <div style={S.avatarLg}>{initials(l.contactName || "")}</div>
              <div>
                <h2 style={{ margin: "0 0 4px", fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>{l.clubName || "—"}</h2>
                <div style={{ color: GOLD, fontSize: 13, marginBottom: 4 }}>{l.contactName || "—"}{(l.title || "").trim() ? " · " + l.title : ""}</div>
                {l.followUp && <div style={{ color: SLATE, fontSize: 12, marginBottom: 4 }}>📅 Follow-up: {fmt(l.followUp)}</div>}
                {rep && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}><div style={{ width: 20, height: 20, borderRadius: "50%", background: rep.color, color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{rep.initials}</div><span style={{ fontSize: 12, color: SLATE }}>Assigned to {rep.name}</span></div>}
                <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>{l.email ? <a href={"mailto:" + l.email} style={{ color: GOLD, fontSize: 13, textDecoration: "none" }}>✉ {l.email}</a> : <span style={{ color: SLATE, fontSize: 13 }}>✉ —</span>}{l.phone && <span style={{ color: SLATE, fontSize: 13 }}>📞 {l.phone}</span>}</div>
              </div>
            </div>
            <div style={S.divider}><div style={S.sectionLabel}>Lead Details</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px 24px" }}>{[{ label: "Inquiry Date", value: fmt(l.createdAt) }, { label: "Lead Source", value: l.source || "—" }, { label: "Service Interest", value: l.service || "—" }, ...(l.renewalDate ? [{ label: "Renewal Date", value: fmt(l.renewalDate) }] : [])].map(({ label, value }) => (<div key={label}><div style={{ fontSize: 11, color: SLATE, marginBottom: 4 }}>{label}</div><div style={{ fontWeight: 700, fontSize: 13 }}>{value}</div></div>))}</div></div>
            <div style={S.divider}><div style={S.sectionLabel}>Deal Value</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}><div><div style={{ fontSize: 11, color: SLATE, marginBottom: 4 }}>Monthly</div><div style={{ fontWeight: 700, fontSize: 20, fontFamily: "Merriweather,serif" }}>{hasDealValue ? fmtVal(dealNum) : "—"}</div></div><div><div style={{ fontSize: 11, color: SLATE, marginBottom: 4 }}>Annual</div><div style={{ fontWeight: 700, fontSize: 20, fontFamily: "Merriweather,serif" }}>{hasDealValue ? fmtVal(dealNum * 12) : "—"}</div></div></div></div>
            {Array.isArray(customLeadFields) && customLeadFields.length > 0 && <div style={S.divider}><div style={S.sectionLabel}>Additional Information</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>{customLeadFields.map(f => (<div key={f.id} style={f.type === "Textarea" ? { gridColumn: "1/-1" } : {}}><label style={S.label}>{f.label}</label><CustomFieldInput field={f} value={(l.customFields || {})[f.id]} onChange={v => updateLeadCustomField(l.id, f.id, v)} /></div>))}</div></div>}
          </>) : (<>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 17, color: NAVY, marginBottom: 20 }}>Edit Lead Profile</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Club Name *</label><input value={F.clubName || ""} onChange={e => set("clubName", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Contact Name</label><input value={F.contactName || ""} onChange={e => set("contactName", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Title</label><input value={F.title || ""} onChange={e => set("title", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Email</label><input type="email" value={F.email || ""} onChange={e => set("email", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Phone</label><input value={F.phone || ""} onChange={e => set("phone", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Stage</label><select value={F.stage || ""} onChange={e => set("stage", e.target.value)} style={S.input}>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label style={S.label}>Lead Source</label><select value={F.source || ""} onChange={e => set("source", e.target.value)} style={S.input}><option value="">— Select —</option>{leadSources.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label style={S.label}>Service Interest</label><select value={F.service || ""} onChange={e => set("service", e.target.value)} style={S.input}><option value="">— Select —</option>{services.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><label style={S.label}>Monthly Value ($)</label><input type="number" value={F.value || ""} onChange={e => set("value", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Inquiry Date</label><input type="date" value={F.createdAt || ""} onChange={e => set("createdAt", e.target.value)} style={S.input} /></div>
              <div><label style={S.label}>Follow-up Date</label><input type="date" value={F.followUp || ""} onChange={e => set("followUp", e.target.value)} onBlur={() => { const updated = leads.map(x => sameId(x.id, l.id) ? { ...x, ...F } : x); saveLeads(updated); setSelectedLead(updated.find(x => sameId(x.id, l.id)) || l); }} style={S.input} /></div>
              <div><label style={S.label}>Renewal Date</label><input type="date" value={F.renewalDate || ""} onChange={e => set("renewalDate", e.target.value)} style={S.input} /></div>
              <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Assigned To</label><select value={F.assignedTo || ""} onChange={e => set("assignedTo", e.target.value)} style={S.input}><option value="">— Unassigned —</option>{users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}</select></div>
            </div>
            {Array.isArray(customLeadFields) && customLeadFields.length > 0 && (
              <div style={S.divider}>
                <div style={S.sectionLabel}>Additional Information</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {customLeadFields.map(f => (
                    <div key={f.id} style={f.type === "Textarea" ? { gridColumn: "1/-1" } : {}}>
                      <label style={S.label}>{f.label}</label>
                      <CustomFieldInput field={f} value={(F.customFields || {})[f.id] || ""} onChange={v => set("customFields", { ...(F.customFields || {}), [f.id]: v })} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>)}
          <AttachmentsPanel attachments={l.attachments || []} onAdd={handleAddAttachment} onDelete={handleDeleteAttachment} />
        </div>
        {showEmailModal && <EmailComposeModal lead={l} currentUser={currentUser} onClose={() => setShowEmailModal(false)} showToast={showToast} saveLeads={saveLeads} saveClients={saveClients} leads={leads} clients={clients} templates={emailTemplates || []} signature={emailSignature} />}
        <ActivityFeed items={activityItems} onAdd={() => { setActivityTarget("lead"); setShowAddActivity(true); }} />
      </div>
    </div>
  );
}
